import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Container, ContainerXScroll } from "../../components/Container/Container";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import type { ColumnMeta } from "../../components/CustomDatatable/CustomDatatable";
import StatusTag from "../../components/StatusTag/StatusTag";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import {
  InstanceDialog,
  ClientDialog,
  SecretDialog,
  GrantRoleDialog,
  ConfirmDialog,
  CLIENT_KIND_LABELS,
} from "../../components/Dialogs/AccessDialogs";
import { formatDateTime, toUserOptions } from "../../components/Access/accessOptions";
import { useInstanceAPI, useProjectRolesAPI } from "../../hooks/api/use-accessAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { InstanceClient, RoleAssignment } from "../../store/types";
import { H2, BodyText } from "../../styles/Typography";
import {
  IconArrowLeft,
  IconCaseFilled,
  IconCompanyFilled,
  IconIdFilled,
  IconLink,
  IconLock,
  IconEdit,
  IconDelete,
  IconPlus,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,

  CardsColumnContainer,
  FlexColumn,
  ButtonContainer,
  ButtonLabel,
} from "../ProjectDetails/ProjectDetails.styles";
import {
  FlexLineJustify,
  PageName,
  Label,
  ButtonBox,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";
import { ActionsRow, InfoColumn, Muted, UriList } from "./InstanceDetails.styles";

type Confirm = "delete" | "disable" | "rotate" | "deleteClient" | null;

const InstanceDetails: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    instance,
    instanceNotFound,
    client,
    assignments,
    refetchAll,
    handleUpdateInstance,
    handleSetEnabled,
    handleDeleteInstance,
    handleCreateClient,
    handleUpdateClient,
    handleRotateSecret,
    handleDeleteClient,
    handleGrantRole,
    handleRevokeRole,
    toastRef,
  } = useInstanceAPI(id);
  const { roles } = useProjectRolesAPI(instance?.projectId);
  const { users } = useUsersAPI();

  const usersOptions = useMemo(() => toUserOptions(users), [users]);
  const rolesOptions = useMemo(
    () => roles.map((role) => ({ label: role.name, value: role.id })),
    [roles]
  );

  const [editVisible, setEditVisible] = useState(false);
  const [clientDialogVisible, setClientDialogVisible] = useState(false);
  const [grantVisible, setGrantVisible] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [revoking, setRevoking] = useState<RoleAssignment | null>(null);
  // клиент с секретом: показывается один раз после создания или ротации
  const [secret, setSecret] = useState<InstanceClient | null>(null);

  if (instanceNotFound)
    return (
      <Container>
        <H2>Инстанс не найден</H2>
        <Button outlined onClick={() => navigate(-1)} label="Назад" />
      </Container>
    );

  if (!instance)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  const assignmentColumns: ColumnMeta<RoleAssignment>[] = [
    { field: "userEmail", header: "Пользователь", sortable: true },
    { field: "roleName", header: "Роль", sortable: true, body: (x) => <code>{x.roleName}</code> },
    { field: "grantedAt", header: "Назначена", sortable: true, body: (x) => formatDateTime(x.grantedAt) },
  ];

  const confirmTexts: Record<Exclude<Confirm, null>, { header: string; text: string; label: string }> = {
    delete: {
      header: "Удалить инстанс",
      text: "Инстанс будет удалён вместе с клиентом и всеми назначениями ролей. Вход в приложение через ComradeMajor прекратится.",
      label: "Удалить",
    },
    disable: {
      header: "Выключить инстанс",
      text: "Новые входы и обновление токенов для инстанса прекратятся, токены его клиента будут отозваны сразу.",
      label: "Выключить",
    },
    rotate: {
      header: "Выпустить новый секрет",
      text: "Старый секрет и все токены клиента перестанут действовать сразу. Новый секрет нужно будет прописать на сервере компании.",
      label: "Выпустить",
    },
    deleteClient: {
      header: "Удалить клиента",
      text: "Вход через сервер инстанса и служебный доступ прекратятся, токены клиента будут отозваны.",
      label: "Удалить",
    },
  };

  const runConfirm = async () => {
    switch (confirm) {
      case "delete": {
        const ok = await handleDeleteInstance();
        if (ok) navigate(`/projects/${instance.projectId}`);
        return ok;
      }
      case "disable": {
        const ok = await handleSetEnabled(false);
        if (ok) refetchAll();
        return ok;
      }
      case "rotate": {
        const rotated = await handleRotateSecret();
        if (rotated) setSecret(rotated);
        return !!rotated;
      }
      case "deleteClient": {
        const ok = await handleDeleteClient();
        if (ok) refetchAll();
        return ok;
      }
      default:
        return true;
    }
  };

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <InfoColumn>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные инстанса</H2>
              <ButtonBox>
                <Button text onClick={() => setEditVisible(true)}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={() => setConfirm("delete")}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </FlexLineJustify>
            <FlexColumn>
              <StyledIconLine icon={<IconIdFilled size={20} />} text={instance.id} />
              <StyledIconLine
                icon={<IconCaseFilled size={20} />}
                text={`Продукт: ${instance.projectName ?? instance.projectId}`}
              />
              <StyledIconLine
                icon={<IconCompanyFilled size={20} />}
                text={`Компания: ${instance.organizationName ?? instance.organizationId}`}
              />
              <StyledIconLine icon={<IconLink size={20} />} text={instance.baseUrl || "Адрес не указан"} />
              {instance.description && <Muted>{instance.description}</Muted>}
              <Muted>
                Ресурс (aud): <code>{instance.resource}</code>
              </Muted>
              <ActionsRow>
                {instance.isEnabled ? (
                  <StatusTag status="success" label="Включён" />
                ) : (
                  <StatusTag status="danger" label="Выключен" />
                )}
                {instance.isEnabled ? (
                  <Button label="Выключить" size="small" outlined severity="danger" onClick={() => setConfirm("disable")} />
                ) : (
                  <Button
                    label="Включить"
                    size="small"
                    outlined
                    onClick={async () => {
                      if (await handleSetEnabled(true)) refetchAll();
                    }}
                  />
                )}
              </ActionsRow>
            </FlexColumn>
          </Container>

          <Container>
            <FlexLineJustify>
              <H2>Клиент</H2>
              {client && (
                <ButtonBox>
                  {client.kind !== "service" && (
                    <Button text onClick={() => setClientDialogVisible(true)} tooltip="Адреса">
                      <IconEdit size={16} />
                    </Button>
                  )}
                  <Button text onClick={() => setConfirm("deleteClient")} tooltip="Удалить клиента">
                    <IconDelete size={16} />
                  </Button>
                </ButtonBox>
              )}
            </FlexLineJustify>
            {client ? (
              <FlexColumn>
                <StyledIconLine icon={<IconIdFilled size={20} />} text={client.clientId} />
                <Muted>Вид: {CLIENT_KIND_LABELS[client.kind]}</Muted>
                {client.redirectUris.length > 0 && (
                  <>
                    <Muted>Возврат после входа:</Muted>
                    <UriList>
                      {client.redirectUris.map((uri) => (
                        <li key={uri}>
                          <code>{uri}</code>
                        </li>
                      ))}
                    </UriList>
                  </>
                )}
                {client.postLogoutRedirectUris.length > 0 && (
                  <>
                    <Muted>Возврат после выхода:</Muted>
                    <UriList>
                      {client.postLogoutRedirectUris.map((uri) => (
                        <li key={uri}>
                          <code>{uri}</code>
                        </li>
                      ))}
                    </UriList>
                  </>
                )}
                {client.kind !== "spa" && (
                  <ActionsRow>
                    <Button size="small" outlined onClick={() => setConfirm("rotate")}>
                      <IconLock size={14} />
                      <ButtonLabel>Новый секрет</ButtonLabel>
                    </Button>
                  </ActionsRow>
                )}
              </FlexColumn>
            ) : (
              <FlexColumn>
                <BodyText>
                  Клиента нет. Он нужен веб-приложению с бэкендом, SPA или бэкенду, которому нужен
                  фид блокировок. Десктоп входит клиентом продукта и своего клиента не требует.
                </BodyText>
                <ActionsRow>
                  <Button onClick={() => setClientDialogVisible(true)}>
                    <IconPlus size={16} />
                    <Label>Создать клиента</Label>
                  </Button>
                </ActionsRow>
              </FlexColumn>
            )}
          </Container>
        </CardsColumnContainer>

        <ButtonContainer>
          <Button outlined onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} />
            <ButtonLabel>Назад</ButtonLabel>
          </Button>
        </ButtonContainer>
      </InfoColumn>

      <ContainerXScroll>
        <FlexLineJustify>
          <PageName>
            <H2>Роли на инстансе</H2>
          </PageName>
          <Button onClick={() => setGrantVisible(true)}>
            <IconPlus size={16} />
            <Label>Назначить роль</Label>
          </Button>
        </FlexLineJustify>
        <CustomDatatable<RoleAssignment>
          data={assignments}
          columns={assignmentColumns}
          onDelete={setRevoking}
          getRowId={(x) => x.id}
          hideEditButton
          hideContextMenu
          sortable
        />
      </ContainerXScroll>

      <InstanceDialog
        visible={editVisible}
        instance={instance}
        onHide={() => setEditVisible(false)}
        onUpdate={async (data) => {
          const ok = await handleUpdateInstance(data);
          if (ok) refetchAll();
          return ok;
        }}
      />

      <ClientDialog
        visible={clientDialogVisible}
        client={client}
        baseUrl={instance.baseUrl}
        onHide={() => setClientDialogVisible(false)}
        onCreate={async (data) => {
          const created = await handleCreateClient(data);
          if (!created) return false;
          if (created.clientSecret) setSecret(created);
          refetchAll();
          return true;
        }}
        onUpdate={async (redirectUris, postLogoutRedirectUris) => {
          const ok = await handleUpdateClient(redirectUris, postLogoutRedirectUris);
          if (ok) refetchAll();
          return ok;
        }}
      />

      <SecretDialog
        client={secret}
        onHide={() => {
          setSecret(null);
          refetchAll();
        }}
      />

      <GrantRoleDialog
        visible={grantVisible}
        usersOptions={usersOptions}
        rolesOptions={rolesOptions}
        onHide={() => setGrantVisible(false)}
        onGrant={async (userId, roleId) => {
          const ok = await handleGrantRole(userId, roleId);
          if (ok) refetchAll();
          return ok;
        }}
      />

      <ConfirmDialog
        visible={!!revoking}
        header="Снять роль"
        text={`Снять роль «${revoking?.roleName}» с ${revoking?.userEmail}?`}
        confirmLabel="Снять"
        onHide={() => setRevoking(null)}
        onConfirm={async () => {
          const ok = await handleRevokeRole(revoking!);
          if (ok) refetchAll();
          return ok;
        }}
      />

      <ConfirmDialog
        visible={!!confirm}
        header={confirm ? confirmTexts[confirm].header : ""}
        text={confirm ? confirmTexts[confirm].text : ""}
        confirmLabel={confirm ? confirmTexts[confirm].label : ""}
        onHide={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </ContainerFlex>
  );
};

export default InstanceDetails;
